import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  Chip,
  Typography,
  CircularProgress,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  TablePagination,
} from '@material-ui/core';
import toast from 'react-hot-toast';
import { makeStyles } from '@material-ui/core/styles';
import {
  People,
  TrendingUp,
  Visibility,
  GpsFixed,
  Refresh,
  Favorite,
  Search,
  Clear,
} from '@material-ui/icons';
import Chart from 'react-apexcharts';
import {
  creatorsFetch,
  ytCreatorsFetch,
  creatorsAnalyticsInstagramFetch,
  creatorsAnalyticsYoutubeFetch,
  updateProfileData,
  getBrandOverlapCount,
} from 'api_handlers/competitor';
import { getCurrentUser } from 'api_handlers/auth';
import {
  formatNumber,
  formatPercentage,
} from '../lib/utils';
import { DashboardMetricCard } from './DashboardMetricCard';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
  },
  // Keep the sort arrow visible (faded) on every sortable header so users know
  // the columns can be sorted, and full-strength on the active column.
  sortLabel: {
    '& .MuiTableSortLabel-icon': { opacity: 0.4, transition: 'opacity 0.2s' },
    '&:hover .MuiTableSortLabel-icon': { opacity: 0.7 },
    '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { opacity: 1, color: theme.palette.primary.main },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  paginationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  emptyContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  card: {
    height: '100%',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  },
  chartCard: {
    height: '100%',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  },
  overlapSectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  networkContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  networkSvg: {
    width: '100%',
    maxWidth: 500,
    height: 250,
  },
  overlapTable: {
    borderCollapse: 'separate',
    borderSpacing: '4px',
    width: '100%',
    fontSize: '0.75rem',
    '& .MuiTableCell-root': {
      padding: theme.spacing(1),
      borderBottom: 'none',
      borderRadius: 4,
      textAlign: 'center',
      fontSize: '0.75rem',
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
  },
  overlapBrandCell: {
    fontWeight: 600,
    textAlign: 'left !important',
  },
  exclusiveCell: {
    fontWeight: 600,
    fontSize: '0.625rem',
    padding: '4px 8px',
    borderRadius: 4,
    display: 'inline-block',
  },
  overlapCell: {
    fontSize: '0.625rem',
    padding: '4px 8px',
    borderRadius: 4,
    display: 'inline-block',
  },
  diagonalCell: {
    color: theme.palette.text.disabled,
  },
  overlapNote: {
    fontSize: '0.625rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  insightBox: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  insightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  insightText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
}));

const calculateEngagementRate = (likes, comments, shares, views) => {
  likes = likes !== null ? likes : 0;
  comments = comments !== null ? comments : 0;
  shares = shares !== null ? shares : 0;
  if (views === 0 || views === null) {
    return 0;
  }
  const engagementRate = ((likes + comments + shares) / views) * 100;
  return parseFloat(engagementRate.toFixed(2));
};

const getTier = (followers = 0) =>
  followers >= 1000000
    ? 'Mega'
    : followers >= 100000
    ? 'Macro'
    : followers >= 10000
    ? 'Micro'
    : followers >= 2000
    ? 'Nano'
    : 'Normal';

const getBrandColor = (brandName, index) => {
  const colorMap = {
    0: '#f9a620',
    1: '#ffd449',
    2: '#aab03c',
    3: '#548c2f',
    4: '#000000',
  };
  return colorMap[index] || colorMap[0];
};

// Avatars are immutable S3 URLs. A per-render Date.now() cache-buster changed the
// <img> src on every re-render (search/sort/filter/pagination), aborting the load and
// blanking the avatar. Return the URL unchanged so it stays stable and cacheable.
const addCacheBuster = (url) => url || '';

// Category ID to Name mapping
const CATEGORY_MAP = {
  '9241': 'Aviation',
  '4': 'Beauty',
  '6': 'Sports',
  '6806': 'Comedy',
  '9076': 'Entrepreneur',
  '9274': 'Vlogging',
  '9275': 'Comedy/Entertainment',
  '11': 'Dance',
  '9276': 'Community',
  '14': 'Actor',
  '16': 'Automobile',
  '9277': 'Food/Cooking',
  '18': 'Fashion',
  '20': 'Fitness',
  '31': 'Finance',
  '9142': 'Pet Page',
  '9175': 'Daily Vlogger',
  '76': 'Parenting',
  '382': 'Motivation',
  '9208': 'Other',
  '9278': 'Tech/Gadgets',
  '9279': 'Business/Entrepreneurship',
  '9280': 'Automotive',
  '9281': 'Couple',
  '9282': 'Health & Wellness',
  '9283': 'Documentary',
  '9307': 'Art',
  '9308': 'News',
  '9309': 'Doctor',
  '9340': 'Advertising',
  '9373': 'Poetry',
  '9374': 'Anime',
  '9375': 'Podcasting',
  '9406': 'ASMR',
  '9439': 'Shopping',
  '9472': 'Law',
  '9505': 'Celebrity',
  '9538': 'Kids Learning',
  '9571': 'Celebrity/Fan',
  '9604': 'Unboxing',
  '9637': 'Home Improvement',
  '9670': 'Acting',
  '9703': 'Science',
  '9736': 'Voiceover',
  '9769': 'Jewelry',
  '9802': 'Grooming',
  '9835': 'Interior Design',
  '9868': 'Real Estate',
  '9901': 'Editing',
  '9902': 'Cinema',
  '9934': 'Spiritual',
  '9967': 'Pet Care',
  '9044': 'Career Coach',
  '9045': 'Fan Page',
  '9046': 'Gadget Review',
  '9047': 'Legal',
  '9048': 'Model',
  '9049': 'Nutrition',
  '9050': 'Product Review',
  '9051': 'Self Help And Motivation',
  '9052': 'Spirituality',
  '9053': 'Stock Market',
  '9054': 'Website And Tools',
  '9112': 'Music',
  '9113': 'Self Help',
  '9114': 'Haircare',
  '9284': 'Mystery',
  '9285': 'Family',
  '9286': 'Kids/Animation',
  '9287': 'Aerospace',
  '9407': 'Podcast',
  '9572': 'Film',
  '9803': 'Architecture',
  '9968': 'Shopping & Retail',
  '8913': 'Education',
  '8916': 'home decor',
  '4897': 'Decor',
  '9288': 'Educational',
  '9289': 'Transport',
  '9573': 'Wildlife',
  '8561': 'Sustainable',
  '8652': 'Fashion Male',
  '8718': 'Pets',
  '8945': 'Skincare',
  '5554': 'Crypto',
  '7620': 'Motivational',
  '8977': 'Agriculture',
  '8978': 'Art And Craft',
  '8979': 'Business mapping',
  '8980': 'Digital Marketing',
  '8981': 'Entertainment',
  '8982': 'Entertainment Page',
  '8983': 'Fan Page',
  '8985': 'Food',
  '8986': 'Gadget revie',
  '8987': 'Gaming',
  '8988': 'Gardening',
  '8989': 'Graphic Designer',
  '8991': 'Health',
  '8992': 'Infotainment',
  '8993': 'Investment',
  '8994': 'Kids',
  '8995': 'Legal',
  '8996': 'Lifestyle',
  '8997': 'Literature',
  '8998': 'Meme Page',
  '8999': 'Mens Grooming',
  '9000': 'Motor Vloggers',
  '9001': 'Movie Or Tv Reviews',
  '9003': 'News And Media',
  '9004': 'Photography',
  '9006': 'Skincare',
  '9007': 'Sustainability',
  '9008': 'Technology',
  '9009': 'Theatre',
  '9010': 'Travel',
  '9011': 'Website & Tool',
  '9012': 'Yoga',
};

// Clean category name by removing numeric prefixes and extracting name
// If category is just an ID (number), look it up in the mapping
const cleanCategoryName = (category) => {
  if (!category) return 'General';
  
  const categoryStr = category.toString().trim();
  
  // If it's just a number (ID only), look it up in the mapping
  if (/^\d+$/.test(categoryStr)) {
    return CATEGORY_MAP[categoryStr] || 'General';
  }
  
  // Handle formats like "9241 Aviation", "4 Beauty", "6806\tComedy" (with tab)
  // First, try to extract the ID and look it up
  const idMatch = categoryStr.match(/^(\d+)/);
  if (idMatch) {
    const id = idMatch[1];
    const mappedName = CATEGORY_MAP[id];
    if (mappedName) {
      return mappedName;
    }
  }
  
  // Remove leading numbers followed by space or tab to extract just the name
  let cleaned = categoryStr.replace(/^\d+[\s\t]+/, '').trim();
  
  // If it contains a tab, split and take the name part (after the ID)
  if (categoryStr.includes('\t')) {
    const parts = categoryStr.split('\t');
    cleaned = parts[parts.length - 1].trim();
  }
  
  // If it contains multiple spaces, try to extract the name part (after the number)
  if (!cleaned && /\d+\s+/.test(categoryStr)) {
    cleaned = categoryStr.replace(/^\d+\s+/, '').trim();
  }
  
  // If after cleaning it's empty or just numbers (ID only), try mapping again
  if (!cleaned || /^\d+$/.test(cleaned)) {
    return CATEGORY_MAP[cleaned] || 'General';
  }
  
  return cleaned;
};

// Sortable columns for the All Influencers table + how to derive each value from a row.
const INFLUENCER_SORT_COLUMNS = [
  { key: 'name', label: 'Name', numeric: false },
  { key: 'handle', label: 'Handle', numeric: false },
  { key: 'genre', label: 'Genre', numeric: false },
  { key: 'category', label: 'Category', numeric: false },
  { key: 'followers', label: 'Followers', numeric: true },
  { key: 'engagementRate', label: 'Engagement Rate', numeric: true },
  { key: 'posts', label: 'Posts', numeric: true },
];

const getInfluencerSortValue = (inf, field, platform) => {
  const followers = inf.followers || inf.subscriber_count || 0;
  const tier =
    followers >= 1000000 ? 'Mega'
    : followers >= 100000 ? 'Macro'
    : followers >= 10000 ? 'Micro'
    : followers >= 2000 ? 'Nano'
    : 'Normal';
  switch (field) {
    case 'name':
      return (inf.display_name || inf.name || inf.handle || '').toLowerCase();
    case 'handle':
      return String((platform === 'youtube' ? inf.name : inf.handle) || '').toLowerCase();
    case 'genre':
      return String(inf.genre || inf.genre_name || tier).toLowerCase();
    case 'category':
      return String(cleanCategoryName(inf.category_name || inf.category)).toLowerCase();
    case 'followers':
      return followers;
    case 'engagementRate':
      return (
        inf.engagement_rate ||
        calculateEngagementRate(inf.likes_count || 0, inf.comments_count || 0, 0, inf.view_count || inf.followers || 1)
      );
    case 'posts':
      return inf.total_posts || inf.posts_count || 0;
    default:
      return 0;
  }
};

export default function InfluencersTab({
  selectedBrand,
  competitorBrands,
  start_date,
  end_date,
  platform = 'instagram',
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [influencers, setInfluencers] = useState([]);
  const [, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [updatingProfiles, setUpdatingProfiles] = useState({}); // Track loading state per influencer
  const [isStaff, setIsStaff] = useState(false);
  const [overlapApiData, setOverlapApiData] = useState({});
  const [overlapAnalyticsData, setOverlapAnalyticsData] = useState({});

  // Filters for the All Influencers table (client-side, over the loaded page)
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [genreFilter, setGenreFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [minFollowers, setMinFollowers] = useState('');
  const [minEngagement, setMinEngagement] = useState('');

  // Client-side pagination over the loaded + filtered set
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting for the All Influencers table
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };
  
  // Get is_staff status from API
  useEffect(() => {
    getCurrentUser().then((data) => setIsStaff(data.data.is_staff));
  }, []);
  
  // Handle update profile
  const handleUpdateProfile = useCallback(async (inf) => {
    const influencerId = inf.id || inf.handle || inf.channel_id;
    setUpdatingProfiles(prev => ({ ...prev, [influencerId]: true }));
    
    try {
      const selectedBrandObj = competitorBrands.find((b) => b.title === selectedBrand);
      const response = await updateProfileData({
        handle: inf.handle || inf.channel_id,
        brand_id: selectedBrandObj?.id || selectedBrandObj?.brand_id,
        profile_type: inf.channel_id ? 'youtube' : 'instagram',
      });
      
      if (response && (response.status === 200 || response.status === 201)) {
        toast.success('Profile updated successfully');
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile. Please try again.');
    } finally {
      setUpdatingProfiles(prev => ({ ...prev, [influencerId]: false }));
    }
  }, [selectedBrand, competitorBrands]);

  // Clear data immediately when brand changes
  useEffect(() => {
    if (selectedBrand) {
      setInfluencers([]);
      setPagination({ count: 0, next: null, previous: null });
      setAnalyticsData(null);
      setSearchQuery('');
      setCategoryFilter('all');
      setGenreFilter('all');
      setBrandFilter('all');
      setMinFollowers('');
      setMinEngagement('');
      setPage(0);
    }
  }, [selectedBrand]);

  // Fetch ALL influencers across every page so filters apply to the full set.
  const fetchInfluencers = useCallback(
    async () => {
      if (!selectedBrand) return;

      // Get fresh brandData from competitorBrands instead of relying on state
      const currentBrandData = competitorBrands.find((b) => b.title === selectedBrand);
      if (!currentBrandData) return;

      setLoading(true);
      try {
        const isYouTube = platform === 'yt' || platform === 'youtube';

        // Build comma-separated handles for all other competitor brands
        const otherBrandHandles = competitorBrands
          .filter((b) => b.title !== selectedBrand)
          .map((b) => (isYouTube
            ? (b.yt_handle && !b.yt_handle.toLowerCase().includes('test') ? b.yt_handle : null)
            : b.ig_handle))
          .filter(Boolean)
          .join(',');

        const ytHandle = currentBrandData.yt_handle || '';
        const igHandle = currentBrandData.ig_handle || '';
        const validYt =
          ytHandle && ytHandle.trim() !== '' && !ytHandle.toLowerCase().includes('test');
        const validIg = igHandle && igHandle.trim() !== '';

        // Fetch a single page of raw creator results for the active platform
        const fetchPage = (page) => {
          if (isYouTube) {
            if (!validYt) return Promise.resolve(null);
            return ytCreatorsFetch(
              ytHandle, start_date, end_date, '', false, ytHandle,
              currentBrandData.excluded_channel_ids, page, otherBrandHandles || null
            );
          }
          if (!validIg) return Promise.resolve(null);
          return creatorsFetch(
            igHandle, start_date, end_date, '', false, igHandle,
            currentBrandData.excluded_handles, page, otherBrandHandles || null
          );
        };

        const firstResponse = await fetchPage(1);
        if (!firstResponse) {
          setInfluencers([]);
          setPagination({ count: 0, next: null, previous: null });
          return;
        }

        let rawResults = [...(firstResponse.results || [])];
        const total = firstResponse.count || rawResults.length;
        const pageSize = (firstResponse.results || []).length || 1;
        const MAX_PAGES = 100; // safety cap to avoid runaway loops
        const totalPages = Math.min(
          pageSize > 0 ? Math.ceil(total / pageSize) : 1,
          MAX_PAGES
        );

        // Fetch remaining pages in parallel
        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p += 1) pagePromises.push(fetchPage(p));
          const responses = await Promise.all(pagePromises);
          responses.forEach((r) => {
            if (r && Array.isArray(r.results)) rawResults.push(...r.results);
          });
        }

        // Apply per-brand exclusions across the full set
        let combinedResults = rawResults;
        if (isYouTube) {
          const excludedYtList =
            (currentBrandData.excluded_channel_ids &&
              currentBrandData.excluded_channel_ids.split(',')) || [];
          if (excludedYtList.length > 0) {
            combinedResults = rawResults.filter(
              (creator) => excludedYtList.indexOf(creator.channel_id) === -1
            );
          }
        } else {
          const excludedList =
            (currentBrandData.excluded_handles &&
              currentBrandData.excluded_handles.split(',')) || [];
          if (excludedList.length > 0) {
            combinedResults = rawResults.filter(
              (creator) => excludedList.indexOf((creator.handle || '').replace('@', '')) === -1
            );
          }
        }

        // Add brand info to each influencer
        const influencersWithBrand = combinedResults.map((inf) => ({
          ...inf,
          brand: selectedBrand,
        }));

        setInfluencers(influencersWithBrand);
        setPagination({ count: influencersWithBrand.length, next: null, previous: null });
      } catch (error) {
        console.error('Error fetching influencers:', error);
        setInfluencers([]);
        setPagination({ count: 0, next: null, previous: null });
      } finally {
        setLoading(false);
      }
    },
    [selectedBrand, competitorBrands, start_date, end_date, platform]
  );

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!selectedBrand) return;

    // Get fresh brandData from competitorBrands instead of relying on state
    const currentBrandData = competitorBrands.find((b) => b.title === selectedBrand);
    if (!currentBrandData) return;

    setAnalyticsLoading(true);
    try {
      const isYouTube = platform === 'yt' || platform === 'youtube';
      let analytics = null;
      let combinedAnalytics = {
        summary: {
          total_influencers: 0,
          avg_engagement: 0,
          total_followers: 0,
          active_campaigns: 0,
          total_views: 0,
          total_engagement: 0,
        },
        category_wise: [],
        genre_wise: [],
      };

      // Fetch analytics for the selected platform only
      if (isYouTube) {
        // Fetch YouTube analytics only
        const ytHandle = currentBrandData.yt_handle || '';
        if (ytHandle && ytHandle.trim() !== '' && !ytHandle.toLowerCase().includes('test')) {
          analytics = await creatorsAnalyticsYoutubeFetch(
            ytHandle,
            start_date,
            end_date,
            '',
            false,
            null,
            currentBrandData.excluded_channel_ids
          );
        }
      } else {
        // Fetch Instagram analytics only
        const igHandle = currentBrandData.ig_handle || '';
        if (igHandle && igHandle.trim() !== '') {
          analytics = await creatorsAnalyticsInstagramFetch(
            igHandle,
            start_date,
            end_date,
            '',
            false,
            null,
            currentBrandData.excluded_handles
          );
        }
      }

      if (analytics) {
        if (analytics.summary) {
          combinedAnalytics.summary.total_influencers = analytics.summary.total_influencers || 0;
          combinedAnalytics.summary.total_followers = analytics.summary.total_followers || 0;
          combinedAnalytics.summary.active_campaigns = analytics.summary.active_campaigns || 0;
          combinedAnalytics.summary.avg_engagement = analytics.summary.avg_engagement || 0;
          combinedAnalytics.summary.total_views = analytics.summary.total_views || 0;
          combinedAnalytics.summary.total_engagement = analytics.summary.total_engagement || 0;
        }
        // Handle category_wise as array
        if (analytics.category_wise && Array.isArray(analytics.category_wise)) {
          analytics.category_wise.forEach((item) => {
            const existing = combinedAnalytics.category_wise.find(c => c.category === item.category);
            if (existing) {
              existing.count += item.count || 0;
            } else {
              combinedAnalytics.category_wise.push({ category: item.category, count: item.count || 0 });
            }
          });
        }
        // Handle genre_wise as array
        if (analytics.genre_wise && Array.isArray(analytics.genre_wise)) {
          analytics.genre_wise.forEach((item) => {
            const existing = combinedAnalytics.genre_wise.find(g => g.genre === item.genre);
            if (existing) {
              existing.count += item.count || 0;
            } else {
              combinedAnalytics.genre_wise.push({ genre: item.genre, count: item.count || 0 });
            }
          });
      }

      setAnalyticsData(combinedAnalytics);
    } }catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedBrand, competitorBrands, start_date, end_date, platform]);

  useEffect(() => {
    if (selectedBrand && competitorBrands.length > 0) {
      const brand = competitorBrands.find((b) => b.title === selectedBrand);
      if (brand) {
        fetchInfluencers();
        fetchAnalytics();
      }
    }
  }, [selectedBrand, competitorBrands, start_date, end_date, fetchInfluencers, fetchAnalytics]);

  // Other competitor brand titles (excluding selected brand)
  const otherBrandTitles = useMemo(
    () => competitorBrands.map((b) => b.title).filter((t) => t !== selectedBrand),
    [competitorBrands, selectedBrand]
  );

  const isValidYtHandle = useCallback(
    (h) => h && h.trim() !== '' && !h.toLowerCase().includes('test'),
    []
  );

  // Fetch overlap between selectedBrand and all other brands
  const fetchBrandOverlap = useCallback(async () => {
    if (!selectedBrand || competitorBrands.length === 0) return;

    const brandData = competitorBrands.find((b) => b.title === selectedBrand);
    if (!brandData) return;

    const brandHandle =
      platform === 'instagram'
        ? brandData.ig_handle
        : isValidYtHandle(brandData.yt_handle)
          ? brandData.yt_handle
          : brandData.handle || selectedBrand;

    if (platform !== 'instagram' && !isValidYtHandle(brandData.yt_handle) && !brandData.handle) {
      setOverlapApiData({ [selectedBrand]: { total: 0, overlaps: {} } });
      return;
    }

    const otherHandles = otherBrandTitles
      .map((t) => {
        const other = competitorBrands.find((c) => c.title === t);
        if (platform === 'instagram') return other?.ig_handle;
        return isValidYtHandle(other?.yt_handle) ? other?.yt_handle : other?.handle || t;
      })
      .filter(Boolean)
      .join(',');

    if (!otherHandles) {
      setOverlapApiData({ [selectedBrand]: { total: 0, overlaps: {} } });
      return;
    }

    try {
      const overlapResponse = await getBrandOverlapCount(
        brandHandle,
        start_date || '',
        end_date || '',
        '',
        otherHandles,
        platform || 'instagram'
      );

      if (overlapResponse && Array.isArray(overlapResponse)) {
        const normalize = (str) => (str || '').toString().toLowerCase().trim();
        const brandEntry = overlapResponse.find((entry) => {
          const entryHandle = normalize(entry.brand_handle || '');
          const entryName = normalize(entry.brand_name || '');
          const searchHandle = normalize(brandHandle);
          const searchTitle = normalize(selectedBrand);
          return (
            entryHandle === searchHandle ||
            entryHandle === searchTitle ||
            entryName === searchHandle ||
            entryName === searchTitle
          );
        });

        if (brandEntry) {
          const overlapCounts = {};
          Object.keys(brandEntry).forEach((key) => {
            if (key !== 'brand_handle' && key !== 'brand_name' && key !== 'total') {
              overlapCounts[key] = brandEntry[key] || 0;
            }
          });
          setOverlapApiData({ [selectedBrand]: { total: brandEntry.total || 0, overlaps: overlapCounts } });
        }
      }
    } catch (error) {
      console.error(`Error fetching overlap for ${selectedBrand}:`, error);
      setOverlapApiData({ [selectedBrand]: { total: 0, overlaps: {} } });
    }
  }, [selectedBrand, otherBrandTitles, competitorBrands, start_date, end_date, platform, isValidYtHandle]);

  // Fetch analytics totals for all brands (for total counts in overlap row)
  const fetchAnalyticsForAllBrands = useCallback(async () => {
    if (competitorBrands.length === 0) return;

    const analyticsMap = {};

    for (const brandData of competitorBrands) {
      try {
        let total = 0;

        if (brandData.ig_handle && brandData.ig_handle.trim() !== '') {
          const igData = await creatorsAnalyticsInstagramFetch(
            brandData.ig_handle, start_date || '', end_date || '', '', false, null,
            brandData.excluded_handles
          );
          if (igData?.summary) total += igData.summary.total_influencers || 0;
        }

        if (isValidYtHandle(brandData.yt_handle)) {
          const ytData = await creatorsAnalyticsYoutubeFetch(
            brandData.yt_handle, start_date || '', end_date || '', '', false, null,
            brandData.excluded_channel_ids
          );
          if (ytData?.summary) total += ytData.summary.total_influencers || 0;
        }

        analyticsMap[brandData.title] = { total_influencers: total };
      } catch (error) {
        console.error(`Error fetching analytics for ${brandData.title}:`, error);
        analyticsMap[brandData.title] = { total_influencers: 0 };
      }
    }

    setOverlapAnalyticsData(analyticsMap);
  }, [competitorBrands, start_date, end_date, isValidYtHandle]);

  useEffect(() => {
    if (selectedBrand && competitorBrands.length > 1) {
      fetchBrandOverlap();
    }
  }, [selectedBrand, competitorBrands, start_date, end_date, platform, fetchBrandOverlap]);

  useEffect(() => {
    if (competitorBrands.length > 0) {
      fetchAnalyticsForAllBrands();
    }
  }, [competitorBrands, start_date, end_date, fetchAnalyticsForAllBrands]);

  // Build overlap row for the selectedBrand only
  const overlapMatrix = useMemo(() => {
    if (!selectedBrand || otherBrandTitles.length === 0) return [];

    const selectedIndex = competitorBrands.findIndex((b) => b.title === selectedBrand);
    const overlapInfo = overlapApiData[selectedBrand];
    const total =
      overlapInfo?.total ||
      overlapAnalyticsData[selectedBrand]?.total_influencers ||
      0;

    const normalizeStr = (str) => (str || '').toString().toLowerCase().trim();

    const findOverlapCount = (overlapData, otherBrandTitle) => {
      if (!overlapData || !otherBrandTitle) return 0;
      const otherD = competitorBrands.find((b) => b.title === otherBrandTitle);
      const possibleKeys = [
        otherBrandTitle,
        otherD?.ig_handle,
        otherD?.yt_handle,
        otherD?.handle,
      ].filter(Boolean);

      for (const key of possibleKeys) {
        if (Object.prototype.hasOwnProperty.call(overlapData, key)) return overlapData[key] || 0;
      }
      const normalizedOther = normalizeStr(otherBrandTitle);
      for (const [key, value] of Object.entries(overlapData)) {
        if (normalizeStr(key) === normalizedOther) return value || 0;
      }
      const normalizedPossible = possibleKeys.map(normalizeStr);
      for (const [key, value] of Object.entries(overlapData)) {
        if (normalizedPossible.includes(normalizeStr(key))) return value || 0;
      }
      return 0;
    };

    const overlaps = {};
    let totalShared = 0;

    otherBrandTitles.forEach((otherBrand) => {
      const count =
        overlapInfo?.overlaps && Object.keys(overlapInfo.overlaps).length > 0
          ? findOverlapCount(overlapInfo.overlaps, otherBrand)
          : 0;
      overlaps[otherBrand] = count;
      totalShared += count;
    });

    return [
      {
        brand: selectedBrand,
        total,
        exclusive: Math.max(0, total - totalShared),
        shared: totalShared,
        overlaps,
        brandIndex: selectedIndex >= 0 ? selectedIndex : 0,
      },
    ];
  }, [selectedBrand, otherBrandTitles, overlapApiData, overlapAnalyticsData, competitorBrands]);

  // Hub-and-spoke SVG positions: selectedBrand at center, others around it
  const networkPositions = useMemo(() => {
    const positions = {};
    const centerX = 200;
    const centerY = 150;
    const radius = 90;

    if (selectedBrand) {
      positions[selectedBrand] = { x: centerX, y: centerY };
    }
    otherBrandTitles.forEach((brand, index) => {
      const angle = (2 * Math.PI * index) / otherBrandTitles.length - Math.PI / 2;
      positions[brand] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
    return positions;
  }, [selectedBrand, otherBrandTitles]);

  // Background-fetch page 1 of each other brand's creator list to build handle sets

  // Generate key insight text for the selected brand

  const handleRefresh = () => {
    fetchInfluencers();
    fetchAnalytics();
    toast.success('Refreshed');
  };

  // Calculate metrics from analytics data or influencers
  const metrics = useMemo(() => {
    // Use analytics data if available
    if (analyticsData?.summary) {
      return {
        totalInfluencers: analyticsData.summary.total_influencers || 0,
        avgEngagement: analyticsData.summary.avg_engagement || 0,
        totalViews: analyticsData.summary.total_views || 0,
        totalEngagement: analyticsData.summary.total_engagement || 0,
        activeCampaigns: analyticsData.summary.active_campaigns || 0,
      };
    }

    // Fallback to calculating from influencers
    if (influencers.length === 0) {
      return {
        totalInfluencers: 0,
        avgEngagement: 0,
        totalViews: 0,
        totalEngagement: 0,
        activeCampaigns: 12, // Default value
      };
    }

    const totalViews = influencers.reduce((sum, inf) => {
      return sum + (inf.view_count || inf.total_views || 0);
    }, 0);

    const totalEngagementCount = influencers.reduce((sum, inf) => {
      const likes = inf.likes_count || 0;
      const comments = inf.comments_count || 0;
      const shares = inf.shares_count || 0;
      return sum + likes + comments + shares;
    }, 0);

    const totalEngagementRate = influencers.reduce((sum, inf) => {
      const engagementRate =
        inf.engagement_rate ||
        calculateEngagementRate(
          inf.likes_count || 0,
          inf.comments_count || 0,
          0,
          inf.view_count || inf.followers || 1
        );
      return sum + engagementRate;
    }, 0);

    return {
      totalInfluencers: influencers.length,
      avgEngagement: totalEngagementRate / influencers.length,
      totalViews,
      totalEngagement: totalEngagementCount,
      activeCampaigns: 12, // Default value
    };
  }, [analyticsData, influencers]);

  // Calculate category-wise influencer count
  const categoryData = useMemo(() => {
    // Use analytics data if available (new API structure with category_wise array)
    if (analyticsData?.category_wise && Array.isArray(analyticsData.category_wise)) {
      return analyticsData.category_wise.map((item) => ({
        category: cleanCategoryName(item.category),
        count: item.count || 0,
      }));
    }

    // Fallback to calculating from influencers
    const categories = {};
    influencers.forEach((inf) => {
      const category = cleanCategoryName(inf.category_name || inf.category);
      categories[category] = (categories[category] || 0) + 1;
    });
    return Object.entries(categories).map(([category, count]) => ({
      category,
      count,
    }));
  }, [analyticsData, influencers]);

  // Calculate genre-wise influencer count
  const genreData = useMemo(() => {
    // Use analytics data if available (new API structure with genre_wise array)
    if (analyticsData?.genre_wise && Array.isArray(analyticsData.genre_wise)) {
      return analyticsData.genre_wise.map((item) => ({
        genre: item.genre,
        count: item.count || 0,
      }));
    }

    // Fallback to calculating from influencers
    const genres = {};
    influencers.forEach((inf) => {
      // Try to get genre from API data first
      const genre = inf.genre || inf.genre_name;
      
      if (genre) {
        genres[genre] = (genres[genre] || 0) + 1;
      } else {
        // Fallback to calculating tier-based genre
        const followers = inf.followers || inf.subscriber_count || 0;
        const tier =
          followers >= 1000000
            ? 'Mega'
            : followers >= 100000
            ? 'Macro'
            : followers >= 10000
            ? 'Micro'
            : followers >= 2000
            ? 'Nano'
            : 'Normal';
        genres[tier] = (genres[tier] || 0) + 1;
      }
    });
    return Object.entries(genres).map(([genre, count]) => ({
      genre,
      count,
    }));
  }, [analyticsData, influencers]);

  // Distinct filter options derived from the currently loaded influencers
  const filterOptions = useMemo(() => {
    const categories = new Set();
    const genres = new Set();
    const brands = new Set();
    influencers.forEach((inf) => {
      categories.add(cleanCategoryName(inf.category_name || inf.category));
      const followers = inf.followers || inf.subscriber_count || 0;
      genres.add(inf.genre || inf.genre_name || getTier(followers));
      const brandsList =
        Array.isArray(inf.brands) && inf.brands.length > 0 ? inf.brands : [selectedBrand];
      brandsList.forEach((b) => b && brands.add(b));
    });
    return {
      categories: Array.from(categories).sort(),
      genres: Array.from(genres).sort(),
      brands: Array.from(brands).sort(),
    };
  }, [influencers, selectedBrand]);

  const hasActiveFilters =
    searchQuery !== '' ||
    categoryFilter !== 'all' ||
    genreFilter !== 'all' ||
    brandFilter !== 'all' ||
    minFollowers !== '' ||
    minEngagement !== '';

  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setCategoryFilter('all');
    setGenreFilter('all');
    setBrandFilter('all');
    setMinFollowers('');
    setMinEngagement('');
    setPage(0);
  }, []);

  // Apply all filters to the loaded page of influencers
  const filteredInfluencers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const minFollowersNum = minFollowers === '' ? null : Number(minFollowers);
    const minEngagementNum = minEngagement === '' ? null : Number(minEngagement);

    return influencers.filter((inf) => {
      const name = (inf.display_name || inf.name || inf.handle || '').toLowerCase();
      const handle = (inf.handle || inf.name || '').toLowerCase();
      if (query && !name.includes(query) && !handle.includes(query)) return false;

      const category = cleanCategoryName(inf.category_name || inf.category);
      if (categoryFilter !== 'all' && category !== categoryFilter) return false;

      const followers = inf.followers || inf.subscriber_count || 0;
      const genre = inf.genre || inf.genre_name || getTier(followers);
      if (genreFilter !== 'all' && genre !== genreFilter) return false;

      const brandsList =
        Array.isArray(inf.brands) && inf.brands.length > 0 ? inf.brands : [selectedBrand];
      if (brandFilter !== 'all' && !brandsList.includes(brandFilter)) return false;

      if (minFollowersNum !== null && followers < minFollowersNum) return false;

      const engagementRate =
        inf.engagement_rate ||
        calculateEngagementRate(
          inf.likes_count || 0,
          inf.comments_count || 0,
          0,
          inf.view_count || inf.followers || 1
        );
      if (minEngagementNum !== null && engagementRate < minEngagementNum) return false;

      return true;
    });
  }, [
    influencers,
    searchQuery,
    categoryFilter,
    genreFilter,
    brandFilter,
    minFollowers,
    minEngagement,
    selectedBrand,
  ]);

  // Reset to first page whenever the filtered result set changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, categoryFilter, genreFilter, brandFilter, minFollowers, minEngagement]);

  // Sorted view of the filtered influencers (before pagination)
  const sortedInfluencers = useMemo(() => {
    if (!sortField) return filteredInfluencers;
    const arr = [...filteredInfluencers];
    arr.sort((a, b) => {
      const av = getInfluencerSortValue(a, sortField, platform);
      const bv = getInfluencerSortValue(b, sortField, platform);
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filteredInfluencers, sortField, sortDir, platform]);

  // Current page slice of the sorted + filtered influencers
  const paginatedInfluencers = useMemo(
    () => sortedInfluencers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedInfluencers, page, rowsPerPage]
  );


  const COLORS = ['#f9a620', '#ffd449', '#aab03c', '#548c2f', '#000000'];

  if (!selectedBrand) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.emptyContainer}>
          <Typography variant="h6" color="textSecondary">
            Please select a brand to view influencers
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (loading && influencers.length === 0) {
    return (
      <Paper className={classes.tableContainer}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Box style={{ marginTop: 24 }}>
      {/* Metric Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={People}
            title="Total Influencers"
            value={metrics.totalInfluencers.toString()}
            subtitle="Active partners"
            trend="+15.3%"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={TrendingUp}
            title="Avg Engagement"
            value={formatPercentage(metrics.avgEngagement)}
            subtitle="Across all influencers"
            trend="+2.1%"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={Visibility}
            title="Total Views"
            value={formatNumber(metrics.totalViews)}
            subtitle="Combined views"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={Favorite}
            title="Total Engagement"
            value={formatNumber(metrics.totalEngagement)}
            subtitle="Likes + Comments + Shares"
            iconColor="#fef3c7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DashboardMetricCard
            icon={GpsFixed}
            title="Active Campaigns"
            value={metrics.activeCampaigns.toString()}
            subtitle="Running now"
            iconColor="#fef3c7"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Genre-wise Influencers (content verticals) */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 600, fontSize: '16px' }}>
                  Genre-wise Influencers
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 0 }}>
                  Distribution by content genre
                </Typography>
              }
            />
            <CardContent>
              <Chart
                options={{
                  chart: {
                    type: 'bar',
                    toolbar: { show: false },
                    background: 'transparent',
                  },
                  plotOptions: {
                    bar: {
                      borderRadius: 8,
                      columnWidth: '60%',
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    style: {
                      fontSize: '12px',
                      fontWeight: 500,
                      colors: ['#111827'],
                    },
                    offsetY: -5,
                  },
                  xaxis: {
                    categories: categoryData.sort((a, b) => b.count - a.count).slice(0, 10).map((d) => d.category),
                    labels: {
                      style: {
                        colors: '#6b7280',
                        fontSize: '12px',
                      },
                    },
                  },
                  yaxis: {
                    title: {
                      text: 'Number of Influencers',
                      style: {
                        color: '#6b7280',
                        fontSize: '12px',
                      },
                    },
                    labels: {
                      style: {
                        colors: '#6b7280',
                        fontSize: '12px',
                      },
                    },
                  },
                  colors: COLORS,
                  grid: {
                    borderColor: '#e5e7eb',
                    strokeDashArray: 3,
                    xaxis: {
                      lines: {
                        show: false,
                      },
                    },
                    yaxis: {
                      lines: {
                        show: true,
                      },
                    },
                  },
                  theme: { mode: 'light' },
                  tooltip: {
                    theme: 'light',
                  },
                }}
                series={[
                  {
                    name: 'Influencers',
                    data: categoryData.sort((a, b) => b.count - a.count).slice(0, 10).map((d) => d.count),
                  },
                ]}
                type="bar"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Category-wise Influencers (follower tiers) */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardHeader
              title={
                <Typography variant="h6" style={{ fontWeight: 600, fontSize: '16px' }}>
                  Category-wise Influencers
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 0 }}>
                  Distribution by influencer tier
                </Typography>
              }
            />
            <CardContent>
              <Chart
                options={{
                  chart: {
                    type: 'bar',
                    toolbar: { show: false },
                    background: 'transparent',
                  },
                  plotOptions: {
                    bar: {
                      borderRadius: 8,
                      columnWidth: '60%',
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    style: {
                      fontSize: '12px',
                      fontWeight: 500,
                      colors: ['#111827'],
                    },
                    offsetY: -5,
                  },
                  xaxis: {
                    categories: genreData.map((d) => d.genre),
                    labels: {
                      style: {
                        colors: '#6b7280',
                        fontSize: '12px',
                      },
                      rotate: -45,
                      rotateAlways: true,
                    },
                  },
                  yaxis: {
                    title: {
                      text: 'Number of Influencers',
                      style: {
                        color: '#6b7280',
                        fontSize: '12px',
                      },
                    },
                    labels: {
                      style: {
                        colors: '#6b7280',
                        fontSize: '12px',
                      },
                    },
                  },
                  colors: COLORS,
                  grid: {
                    borderColor: '#e5e7eb',
                    strokeDashArray: 3,
                    xaxis: {
                      lines: {
                        show: false,
                      },
                    },
                    yaxis: {
                      lines: {
                        show: true,
                      },
                    },
                  },
                  theme: { mode: 'light' },
                  tooltip: {
                    theme: 'light',
                  },
                }}
                series={[
                  {
                    name: 'Influencers',
                    data: genreData.map((d) => d.count),
                  },
                ]}
                type="bar"
                height={250}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Brand Overlap Section */}
      {selectedBrand && overlapMatrix.length > 0 && otherBrandTitles.length > 0 && (
        <Card className={classes.chartCard} style={{ marginTop: 24 }}>
          <CardHeader
            title={
              <Typography variant="h6" style={{ fontWeight: 600, fontSize: '16px' }}>
                Brand Overlap
              </Typography>
            }
            subheader={
              <Typography variant="caption" color="textSecondary">
                How {selectedBrand} shares influencers with competitors. Numbers on lines = shared influencers.
              </Typography>
            }
          />
          <CardContent style={{ paddingTop: 0 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Left: SVG graph */}
              <Grid item xs={12} md={6}>
                <Box className={classes.networkContainer}>
                  <svg viewBox="0 0 400 300" className={classes.networkSvg}>
                    {otherBrandTitles.map((otherBrand) => {
                      const overlap = overlapMatrix[0]?.overlaps[otherBrand] || 0;
                      const pos1 = networkPositions[selectedBrand];
                      const pos2 = networkPositions[otherBrand];
                      if (!pos1 || !pos2) return null;
                      const selectedIdx = competitorBrands.findIndex((b) => b.title === selectedBrand);
                      const strokeWidth = Math.max(1, Math.min(overlap / 3, 8));
                      return (
                        <g key={otherBrand}>
                          <line
                            x1={pos1.x} y1={pos1.y}
                            x2={pos2.x} y2={pos2.y}
                            stroke={getBrandColor(selectedBrand, selectedIdx)}
                            strokeWidth={overlap > 0 ? strokeWidth : 1}
                            strokeDasharray={overlap === 0 ? '4 4' : undefined}
                            opacity={overlap > 0 ? 0.5 : 0.2}
                          />
                          {overlap > 0 && (() => {
                            const mx = (pos1.x + pos2.x) / 2;
                            const my = (pos1.y + pos2.y) / 2;
                            return (
                              <g>
                                <rect x={mx - 12} y={my - 10} width={24} height={18} rx={4} fill="white" stroke="#e5e7eb" strokeWidth={1} />
                                <text x={mx} y={my} fill="#374151" fontSize="11" fontWeight="700" textAnchor="middle" dominantBaseline="middle">
                                  {overlap}
                                </text>
                              </g>
                            );
                          })()}
                        </g>
                      );
                    })}

                    {(() => {
                      const pos = networkPositions[selectedBrand];
                      if (!pos) return null;
                      const selectedIdx = competitorBrands.findIndex((b) => b.title === selectedBrand);
                      const color = getBrandColor(selectedBrand, selectedIdx);
                      return (
                        <g key={selectedBrand}>
                          <circle cx={pos.x} cy={pos.y} r={40} fill={color} opacity={0.25} stroke={color} strokeWidth={2.5} />
                          <text x={pos.x} y={pos.y - 6} fill={color} fontSize="11" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                            {selectedBrand}
                          </text>
                          <text x={pos.x} y={pos.y + 10} fill={color} fontSize="12" fontWeight="700" textAnchor="middle" dominantBaseline="middle">
                            {overlapMatrix[0]?.total || 0}
                          </text>
                        </g>
                      );
                    })()}

                    {otherBrandTitles.map((brand) => {
                      const pos = networkPositions[brand];
                      if (!pos) return null;
                      const brandIdx = competitorBrands.findIndex((b) => b.title === brand);
                      const color = getBrandColor(brand, brandIdx);
                      const total = overlapAnalyticsData[brand]?.total_influencers || 0;
                      return (
                        <g key={brand}>
                          <circle cx={pos.x} cy={pos.y} r={28} fill={color} opacity={0.15} stroke={color} strokeWidth={1.5} />
                          <text x={pos.x} y={pos.y - 5} fill={color} fontSize="10" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                            {brand}
                          </text>
                          <text x={pos.x} y={pos.y + 8} fill={color} fontSize="11" fontWeight="600" textAnchor="middle" dominantBaseline="middle">
                            {total}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </Box>
              </Grid>

              {/* Right: overlap table */}
              <Grid item xs={12} md={6}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, fontSize: '0.75rem' }}>Competitor</TableCell>
                        <TableCell align="center" style={{ fontWeight: 600, fontSize: '0.75rem' }}>Their Total</TableCell>
                        <TableCell align="center" style={{ fontWeight: 600, fontSize: '0.75rem' }}>Shared</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {otherBrandTitles.map((brand) => {
                        const brandIdx = competitorBrands.findIndex((b) => b.title === brand);
                        const color = getBrandColor(brand, brandIdx);
                        const shared = overlapMatrix[0]?.overlaps[brand] || 0;
                        const total = overlapAnalyticsData[brand]?.total_influencers || 0;
                        return (
                          <TableRow key={brand}>
                            <TableCell style={{ fontSize: '0.75rem' }}>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                                {brand}
                              </Box>
                            </TableCell>
                            <TableCell align="center" style={{ fontSize: '0.75rem', color: '#6b7280' }}>{total}</TableCell>
                            <TableCell align="center">
                              <Box style={{
                                display: 'inline-block',
                                padding: '2px 10px',
                                borderRadius: 12,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                backgroundColor: shared > 0 ? `${color}20` : '#f3f4f6',
                                color: shared > 0 ? color : '#9ca3af',
                              }}>
                                {shared}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, fontSize: '0.75rem', borderTop: '2px solid #e5e7eb' }}>
                          {selectedBrand} (selected)
                        </TableCell>
                        <TableCell align="center" style={{ fontWeight: 700, fontSize: '0.75rem', borderTop: '2px solid #e5e7eb' }}>
                          {overlapMatrix[0]?.total || 0}
                        </TableCell>
                        <TableCell align="center" style={{ borderTop: '2px solid #e5e7eb' }}>
                          <Box style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                          }}>
                            {overlapMatrix[0]?.exclusive || 0} excl.
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Influencers Table */}
      <Card className={classes.chartCard} style={{ marginTop: 24 }}>
        <CardHeader
          title={
            <Typography variant="h6" style={{ fontWeight: 600, fontSize: '16px' }}>
              All Influencers
            </Typography>
          }
          subheader={
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 0 }}>
              Complete list of your influencer partners
            </Typography>
          }
        />
        <CardContent style={{ padding: 0 }}>
          {/* Filter toolbar */}
          <Box
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              padding: 16,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search name or handle"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              style={{ minWidth: 220 }}
            />
            <TextField
              select
              size="small"
              variant="outlined"
              label="Genre"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Genres</MenuItem>
              {filterOptions.categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              variant="outlined"
              label="Category"
              value={genreFilter}
              onChange={(e) => setGenreFilter(e.target.value)}
              style={{ minWidth: 140 }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {filterOptions.genres.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
            {filterOptions.brands.length > 1 && (
              <TextField
                select
                size="small"
                variant="outlined"
                label="Brand"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <MenuItem value="all">All Brands</MenuItem>
                {filterOptions.brands.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              variant="outlined"
              type="number"
              label="Min followers"
              value={minFollowers}
              onChange={(e) => setMinFollowers(e.target.value)}
              style={{ width: 130 }}
            />
            <TextField
              size="small"
              variant="outlined"
              type="number"
              label="Min eng. %"
              value={minEngagement}
              onChange={(e) => setMinEngagement(e.target.value)}
              style={{ width: 120 }}
            />
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={resetFilters}
                startIcon={<Clear />}
                style={{ textTransform: 'none', color: '#6b7280' }}
              >
                Clear
              </Button>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 'auto' }}
            >
              Showing {filteredInfluencers.length} of {influencers.length} influencers
            </Typography>
          </Box>
          <TableContainer>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
                  {INFLUENCER_SORT_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align="center"
                      style={{ fontWeight: 600 }}
                      sortDirection={sortField === col.key ? sortDir : false}
                    >
                      <TableSortLabel
                        className={classes.sortLabel}
                        active={sortField === col.key}
                        direction={sortField === col.key ? sortDir : 'asc'}
                        hideSortIcon={false}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center" style={{ fontWeight: 600 }}>Brands</TableCell>
                  {isStaff && platform === 'instagram' && (
                    <TableCell align="center" style={{ fontWeight: 600 }}>Actions</TableCell>
                  )}
                  {/* <TableCell style={{ fontWeight: 600 }}>Status</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && influencers.length > 0 ? (
              <TableRow>
                <TableCell colSpan={isStaff && platform === 'instagram' ? 9 : 8} style={{ textAlign: 'center', padding: 40 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredInfluencers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isStaff && platform === 'instagram' ? 9 : 8} style={{ textAlign: 'center', padding: 40 }}>
                  <Typography variant="body1" color="textSecondary">
                    {influencers.length === 0
                      ? `No influencers found for ${selectedBrand}`
                      : 'No influencers match the selected filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedInfluencers.map((inf, index) => {
                const engagementRate =
                  inf.engagement_rate ||
                  calculateEngagementRate(
                    inf.likes_count || 0,
                    inf.comments_count || 0,
                    0,
                    inf.view_count || inf.followers || 1
                  );
                const followers = inf.followers || inf.subscriber_count || 0;
                const tier =
                  followers >= 1000000
                    ? 'Mega'
                    : followers >= 100000
                    ? 'Macro'
                    : followers >= 10000
                    ? 'Micro'
                    : followers >= 2000
                    ? 'Nano'
                    : 'Normal';
                
                // Determine genre from API data or calculate from tier
                // Genre could be in different fields: genre, genre_name, or calculated from tier
                const genre = inf.genre || inf.genre_name || tier;

                return (
                  <TableRow
                    key={inf.id || inf.handle || inf.channel_id || index}
                        hover
                  >
                    <TableCell 
                    onClick={() => {
                      if (platform === 'instagram') {
                        window.open(`https://www.instagram.com/${inf.handle}`, '_blank');
                      } else if (platform === 'youtube') {
                        window.open(`https://www.youtube.com/channel/${inf.channel_id}`, '_blank');
                      } 
                    }}>
                      <Box
                            style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                      >
                        <Avatar
                          src={addCacheBuster(inf.profile_pic_url || inf.profile_picture || inf.thumbnail_url)}
                              style={{ width: 40, height: 40 }}
                            >
                              {(inf.display_name || inf.name || inf.handle || '')
                                .substring(0, 2)
                                .toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" style={{ fontWeight: 500, textAlign: 'center' }}>
                          {inf.display_name || inf.name || inf.handle}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center' }}>
                        {platform === 'youtube' ? inf.name : inf.handle}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={cleanCategoryName(inf.category_name || inf.category)}
                        size="small"
                            style={{
                              backgroundColor: '#e5e7eb',
                              color: '#374151',
                              fontWeight: 500,
                              textAlign: 'center',
                            }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ textAlign: 'center' }}>
                        {genre}
                      </Typography>
                    </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" style={{ fontWeight: 500, textAlign: 'center' }}>
                            {formatNumber(followers)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                      style={{
                              color: engagementRate > 5 ? '#10b981' : '#374151',
                              fontWeight: engagementRate > 5 ? 600 : 400,
                              textAlign: 'center',
                      }}
                    >
                      {formatPercentage(engagementRate)}
                          </Typography>
                    </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" style={{ textAlign: 'center' }}>
                            {inf.total_posts || inf.posts_count || 0}
                          </Typography>
                        </TableCell>
                    <TableCell align="center">
                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                        {(() => {
                          // Use brands array from API response directly
                          const brandsList = Array.isArray(inf.brands) && inf.brands.length > 0
                            ? inf.brands
                            : [selectedBrand];

                          return (
                            <>
                              {brandsList.slice(0, 3).map((brand, idx) => {
                                const brandIdx = competitorBrands.findIndex((b) => b.title === brand);
                                const color = getBrandColor(brand, brandIdx >= 0 ? brandIdx : idx);
                                return (
                                  <Chip
                                    key={idx}
                                    label={brand}
                                    size="small"
                                    style={{
                                      backgroundColor: color + '20',
                                      color,
                                      fontWeight: 500,
                                      fontSize: '0.7rem',
                                      maxWidth: 120,
                                    }}
                                  />
                                );
                              })}
                              {brandsList.length > 3 && (
                                <Chip
                                  label={`+${brandsList.length - 3} more`}
                                  size="small"
                                  style={{
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151',
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    {isStaff && platform === 'instagram' && (
                      <TableCell align="center">
                        {updatingProfiles[inf.id || inf.handle || inf.channel_id] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            onClick={() => handleUpdateProfile(inf)}
                            size="small"
                            title="Update Profile"
                            style={{ color: '#1E9CB2' }}
                          >
                            <Refresh />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                    {/* <TableCell>
                      <Chip
                        label="Active"
                        size="small"
                        style={{
                              backgroundColor: '#d1fae5',
                              color: '#065f46',
                              fontWeight: 500,
                        }}
                      />
                    </TableCell> */}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
          </TableContainer>
        {influencers.length > 0 && (
          <Box className={classes.paginationContainer}>
            <Box className={classes.paginationInfo}>
              <Typography variant="body2" color="textSecondary">
                {hasActiveFilters && filteredInfluencers.length !== influencers.length
                  ? `Showing ${filteredInfluencers.length} of ${influencers.length} influencers`
                  : `Total: ${influencers.length} influencers`}
              </Typography>
            </Box>
            <Box className={classes.paginationInfo}>
              <TablePagination
                component="div"
                count={filteredInfluencers.length}
                page={page}
                onChangePage={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
              <IconButton
                onClick={handleRefresh}
                disabled={loading || analyticsLoading}
                size="small"
                title="Refresh"
                style={{ marginLeft: 8 }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </Box>
        )}
        </CardContent>
      </Card>

    </Box>
  );
}

